import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { StarIcon, StatsIcon, ShoppingCartIcon } from "../../../components/Icons";
import SinglePageDashboard from "../../../drawer/SinglePageDashbord";


const REPORT_SECTIONS = [
  {
    category: "Store",
    label: "Store",
    color: "#185FA5",
    bg: "#E6F1FB",
    icon: ShoppingCartIcon(),
    reports: [
      { id: 1, title: "Daily Sale Report", sub: "Sales by day", badge: "Daily", path: "/daliy/sale-report" },
      { id: 2, title: "Monthly Sale Report", sub: "Sales by month", badge: "Monthly", path: "/monthly/sale-report" },
      { id: 3, title: "Product Sale Report", sub: "Per-product breakdown", badge: "Monthly", path: "/Report/Product-Sale-report" },
      { id: 4, title: "Specific Product Sale", sub: "Single product deep-dive", badge: "Monthly", path: "/Report/Specific-Product-Sale-report" },
      { id: 5, title: "Purchase Product Report ", sub: "Top performers", badge: "Trending", path: "/purchase-product-report" },
      { id: 6, title: "SKU Report", sub: "Sale, purchase qty & avg prices with profit", badge: "Monthly", path: "/sku-report" },
    ],
  },
  {
    category: "Accounts",
    label: "Accounts",
    color: "#993C1D",
    bg: "#FAECE7",
    icon: StatsIcon({ filled: true, color: "#993C1D" }),
    reports: [
      { id: 13, title: "Bad Debt Report", sub: "Outstanding receivables", badge: "General", path: "/bad-debts-report" },
      { id: 14, title: "Area Recovery Report", sub: "Outstanding receivables", badge: "General", path: "/area-recovery-report" },
    ],
  },
];
const BADGE_STYLES = {
  Daily: { background: "#E6F1FB", color: "#0C447C" },
  Monthly: { background: "#EAF3DE", color: "#3B6D11" },
  Yearly: { background: "#EEEDFE", color: "#3C3489" },
  Trending: { background: "#FAEEDA", color: "#854F0B" },
  General: { background: "#FAEEDA", color: "#854F0B" },
};
const ALL_REPORTS = REPORT_SECTIONS.flatMap((s) =>
  s.reports.map((r) => ({ ...r, category: s.category, section: s }))
);

const ReportCard = ({ report, section, starred, onToggleStar }) => {
  const navigate = useNavigate();
  const isStarred = starred.has(report.id);
  const badge = BADGE_STYLES[report.badge] || BADGE_STYLES.Daily;

  return (
    <div
      onClick={() => navigate(report.path)}
      style={{
        background: "#fff",
        border: "0.5px solid #e5e5e5",
        borderRadius: 12,
        padding: "1rem",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        position: "relative",
        transition: "border-color 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = section.color;
        e.currentTarget.style.boxShadow = `0 0 0 3px ${section.bg}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#e5e5e5";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Icon */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ width: 36, height: 36, borderRadius: 8, background: section.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {section.icon}
        </div>
        {/* Star button */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleStar(report.id); }}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}
          title={isStarred ? "Unstar" : "Star report"}
        >
          <StarIcon filled={isStarred} color={section.color} />
        </button>
      </div>

      {/* Title */}
      <p style={{ fontSize: 14, fontWeight: 500, color: "#111", lineHeight: 1.3, margin: 0 }}>{report.title}</p>

      {/* Sub + Badge */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <p style={{ fontSize: 12, color: "#888", margin: 0 }}>{report.sub}</p>
        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 500, ...badge }}>{report.badge}</span>
      </div>
    </div>
  );
};

const SectionGroup = ({ section, reports, starred, onToggleStar }) => (
  <div style={{ marginBottom: "1.5rem" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: "#999", textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>
        {section.label}
      </span>
      <div style={{ flex: 1, height: "0.5px", background: "#e5e5e5" }} />
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
      {reports.map((r) => (
        <ReportCard key={r.id} report={r} section={section} starred={starred} onToggleStar={onToggleStar} />
      ))}
    </div>
  </div>
);

const ReportView = (props) => {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [starred, setStarred] = useState(new Set());

  const toggleStar = (id) => {
    setStarred((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filtered = useMemo(() => {
    return ALL_REPORTS.filter((r) => {
      const matchFilter = activeFilter === "all" || activeFilter === r.category || (activeFilter === "starred" && starred.has(r.id));
      const matchSearch = !search || r.title.toLowerCase().includes(search.toLowerCase()) || r.sub.toLowerCase().includes(search.toLowerCase());
      return matchFilter && matchSearch;
    });
  }, [search, activeFilter, starred]);

  // Group filtered results by section
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      if (!map.has(r.category)) map.set(r.category, { section: r.section, reports: [] });
      map.get(r.category).reports.push(r);
    });
    return map;
  }, [filtered]);

  const FILTERS = [
    { key: "all", label: "All" },
    { key: "Store", label: "Store" },
    { key: "Accounts", label: "Accounts" },
    { key: "POS Order", label: "POS Order" },
    { key: "HR", label: "HR" },
  ];

  return (
    <>
      <SinglePageDashboard header="Reports" />

      <div style={{ padding: "1.5rem", background: "#f6f7fb", minHeight: "100vh" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: "1.5rem" }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 500, color: "#111", margin: 0 }}>Reports</h1>
          </div>

          {/* Search */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "0.5px solid #ddd", borderRadius: 8, padding: "6px 12px", minWidth: 220 }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="6.5" cy="6.5" r="5" stroke="#aaa" strokeWidth="1.5" />
              <path d="M10.5 10.5L14 14" stroke="#aaa" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Report"
              style={{ border: "none", outline: "none", background: "transparent", fontSize: 14, color: "#111", width: "100%" }}
            />
          </div>
        </div>

        {/* Filter pills */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: "1.25rem" }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              style={{
                padding: "6px 14px",
                fontSize: 13,
                borderRadius: 20,
                border: "0.5px solid",
                borderColor: activeFilter === f.key ? "#185FA5" : "#ddd",
                background: activeFilter === f.key ? "#185FA5" : "#fff",
                color: activeFilter === f.key ? "#E6F1FB" : "#666",
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all 0.15s",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Report sections */}
        {grouped.size === 0 ? (
          <p style={{ color: "#aaa", fontSize: 14, textAlign: "center", marginTop: "3rem" }}>No reports match your filter.</p>
        ) : (
          Array.from(grouped.entries()).map(([cat, { section, reports }]) => (
            <SectionGroup
              key={cat}
              section={section}
              reports={reports}
              starred={starred}
              onToggleStar={toggleStar}
            />
          ))
        )}
      </div>
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({});

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(ReportView);